import { useEffect } from 'react';

import { DateTime, Duration } from 'luxon';

import { getRefetchJitterMs } from '@/utils';

/**
 * Invokes `callback` immediately, then repeatedly after `callbackInterval`. Each scheduled
 * interval gets a random 1–20s jitter (see {@link getRefetchJitterMs}) so periodic refetches
 * across widgets/instances don't all fire on the same tick. The immediate first call is not
 * jittered, so initial data still loads right away.
 */
export const usePeriodicCallback = (
  callback: () => void,
  callbackInterval: Duration,
  callerName?: string
) =>
  useEffect(() => {
    console.info(
      `[usePeriodicCallback ${DateTime.now().toFormat('HH:mm:ss')}] ${callerName ?? 'unknown caller'}: invoking callback immediately, starting periodic callback every ${callbackInterval.toFormat('hh:mm:ss')}`
    );
    callback();

    let timeoutId: ReturnType<typeof setTimeout>;
    const scheduleNext = () => {
      timeoutId = setTimeout(() => {
        console.info(
          `[usePeriodicCallback ${DateTime.now().toFormat('HH:mm:ss')}] ${callerName ?? 'unknown caller'}: running scheduled callback ${callbackInterval.toFormat('hh:mm:ss')}`
        );
        callback();
        scheduleNext();
      }, callbackInterval.toMillis() + getRefetchJitterMs());
    };
    scheduleNext();

    return () => clearTimeout(timeoutId);
  }, [callback, callbackInterval, callerName]);
